import {
  createContext, useCallback, useMemo, useState,
} from 'react';
import Template from '../models/PageTemplate/PageTemplate';
import templateAPI from '../services/pageTemplate';
import { PaginationMetaLink } from '../models/Paginator';

interface TemplatesContextProps {
  templateList?: Template[],
  getAllTemplates: (page?: number, perPage?: number, searchText?: string) => Promise<{
    currentPage: number,
    total: number,
    links: PaginationMetaLink[]
  }>,
  getTemplatesFromCategory: (id: string, options:{ page?: number, searchText?: string }) => Promise<{
    currentPage: number,
    data: Template[],
    total: number,
    links: PaginationMetaLink[]
  }>
  submitTemplate: (template: Template, cb?: () => void) => void,
  destroyTemplate: (id: string, cb?: () => void) => void
  loading: boolean,
  errorMessage: string,
}

export const PageTemplatesContext = createContext<TemplatesContextProps>({} as TemplatesContextProps);
PageTemplatesContext.displayName = 'TemplatesContext';

interface Props {
  children: JSX.Element
}

export const PageTemplatesProvider = ({ children }: Props) => {
  const [templateList, setTemplateList] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getAllTemplates = useCallback(async (page?:number, perPage?: number, searchText?: string): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
  }> => {
    setLoading(true);
    const result = await templateAPI.getAllTemplates(page, perPage, searchText);
    setLoading(false);
    setTemplateList(result.data);
    return {
      links: result.meta.links,
      currentPage: result.meta.current_page,
      total: result.meta.total,
    };
  }, []);

  const getTemplatesFromCategory = useCallback(async (categoryId:string, options:{ page?: number, searchText?: string }): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
    data: Template[],
  }> => {
    const { page, searchText } = options;
    const templatesResult = (await templateAPI.getTemplates(categoryId, page, searchText));
    return {
      data: templatesResult.data,
      currentPage: templatesResult.currentPage,
      links: templatesResult.links,
      total: templatesResult.total,
    };
  }, []);

  const submitTemplate = useCallback((data:Template, cb?: () => void) => {
    if (data && data.id) {
      templateAPI.put(data).then(() => {
        console.log('update template successfully');
        // fetch data again
        if (cb != null) {
          cb();
        }
      }).catch(() => {
        console.log('update template int failure');
      });
    } else {
      templateAPI.post(data).then(() => {
        if (cb != null) {
          cb();
        }
      }).catch((err) => {
        console.log('update template in failure');
        setErrorMessage(err.message);
        console.error(err);
      });
    }
  }, []);

  const destroyTemplate = useCallback((id: string, cb?: () => void) => {
    templateAPI.destroy(id).then(() => {
      // fetch data again
      // 既存のブロックリストから削除します。
      setTemplateList((list) => list.filter((l) => l.id !== id));
      cb();
    }).catch((err) => {
      console.error(err);
    });
  }, []);

  const value = useMemo(() => ({
    templateList,
    loading,
    errorMessage,
    getAllTemplates,
    getTemplatesFromCategory,
    destroyTemplate,
    submitTemplate,
  }), [errorMessage, getAllTemplates, getTemplatesFromCategory, destroyTemplate, loading, templateList, submitTemplate]);

  return (
    <PageTemplatesContext.Provider value={value}>
      {children}
    </PageTemplatesContext.Provider>
  );
};
